import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import Text from "@components/Text";
import Switch from "@components/Switch";
import Tooltip from "@components/Tooltip";
import SizedBox from "@components/SizedBox";
import { useLang } from "@src/hooks/useLang";
import {
  getDeviceAlertSettings,
  putDeviceAlertSettings,
} from "@services/deviceAlertsService";
import { ReactComponent as InfoIcon } from "@assets/icons/info.svg";

/**
 * "Alerts about malfunctioning devices" — off by default.
 *
 * Saves immediately rather than waiting for the page's Save button. The units
 * above it are localStorage-only and deliberately deferred; this one is a real
 * server round-trip, and a switch that silently needs a second click elsewhere
 * to take effect is the kind of thing people get wrong once and never trust
 * again.
 *
 * Shown to team members too, and to members viewing an owner's account: the
 * setting belongs to the logged-in person, not the account on screen.
 */

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Callout = styled.div`
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: 12px;
  max-width: 560px;
`;

const HINT =
  "We watch the data coming off your machines and email you when one stops producing usable yield data. " +
  "For potato harvesters we check both sensor channels and the GPS fix, every 2 hours. " +
  "For grain harvesters we check the sensor channel and the GPS fix, once a day. " +
  "You get one email when a problem first appears, and one when it clears — nothing while everything is healthy.";

const DeviceAlertsSection: React.FC = () => {
  const lang = useLang();
  const l = (lang.settings ?? {}) as Record<string, string>;

  const [on, setOn] = useState(false);
  const [emailVerified, setEmailVerified] = useState(true);
  const [email, setEmail] = useState("");
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getDeviceAlertSettings()
      .then((s) => {
        if (cancelled) return;
        setOn(s.malfunctionEmails);
        setEmailVerified(s.emailVerified);
        setEmail(s.email);
      })
      .catch(() => {
        /* api.ts already toasts; leave the switch off and enabled to retry. */
      })
      .finally(() => !cancelled && setReady(true));
    return () => {
      cancelled = true;
    };
  }, []);

  const handleToggle = async () => {
    if (saving || !emailVerified) return;
    const next = !on;
    setOn(next); // optimistic
    setSaving(true);
    try {
      const s = await putDeviceAlertSettings(next);
      setOn(s.malfunctionEmails); // trust the server's echo
    } catch {
      setOn(!next); // revert — api.ts has already surfaced the error
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Text type="heading" size={11} color="#899093" style={{ textTransform: "uppercase" }}>
        {l.notificationsHeading ?? "Notifications"}
      </Text>
      <Text size={14} color="#6B7280" style={{ marginBottom: 16 }}>
        {l.notificationsDescription ?? "Choose what GreenGrowth emails you about."}
      </Text>

      <Row>
        <Text size={14} color="#374151">
          {l.deviceAlerts ?? "Alerts about malfunctioning devices"}
        </Text>
        <Tooltip
          // Hover for mouse, click for touch — an info button that only reacts
          // to hover is unreachable on a phone.
          config={{ trigger: ["hover", "click"], interactive: true, placement: "top" }}
          content={
            <Text size={12} style={{ maxWidth: 320, display: "block" }}>
              {l.deviceAlertsHint ?? HINT}
            </Text>
          }
        >
          <InfoIcon />
        </Tooltip>
        <div style={{ flex: 1 }} />
        <Switch
          on={on}
          disabled={!ready || saving || !emailVerified}
          onToggle={handleToggle}
          ariaLabel={l.deviceAlerts ?? "Alerts about malfunctioning devices"}
        />
      </Row>

      <Text size={12} color="#899093" style={{ marginTop: 4 }}>
        {email
          ? (l.deviceAlertsSentTo ?? "Sent to") + ` ${email}`
          : (l.deviceAlertsDescription ?? "Email me when one of my devices stops working properly.")}
      </Text>

      {ready && !emailVerified && (
        <Callout>
          <Text size={13} color="#92400e">
            {l.deviceAlertsRequiresVerifiedEmail ??
              "Verify your email address before turning this on — otherwise we have nowhere to send the alerts."}
          </Text>
        </Callout>
      )}
      <SizedBox height={8} />
    </>
  );
};

export default DeviceAlertsSection;
